import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listFormSubmissions } from '../../../../../util/APIUtils';
import store, { showToast } from '../../../../core/store';
import { FORM_RESPONSE_PDF_EXPORT_ERROR } from '../../../../Form/constants/form-submission.constants';
import { getFormDefinitionsListCached } from '../../../../Form/utils/formDefinitionsCache.util';
import { exportFormSubmissionAsPdf } from '../../../../Form/utils/form-response-pdf.util';
import {
  loadFormDefinitionWithFallback,
  normalizeSubmissionsResponse,
} from '../../../../Form/utils/form-submission-page.util';
import { FORMS_WIDGET_MESSAGES } from '../../constants/reentry-planning.constants';
import {
  buildFormsRefsKey,
  collectFormsWidgetRefs,
  findSubmissionById,
  hasPersistedFormsWidget,
  indexCatalogById,
  sortSubmissionsNewestFirst,
} from './formsWidgetData.util';
import {
  EMPTY_RESPONSE_CACHE,
  mergeResponseEntries,
  pruneResponseCache,
  selectCurrentSubmissions,
  selectPendingRefs,
} from './formsWidgetResponsesCache.util';
import { notifyFormsWidgetError } from './formsWidgetToast.util';

const CATALOG_STATUS = { IDLE: 'idle', LOADING: 'loading', LOADED: 'loaded', ERROR: 'error' };

const fetchFormSubmissions = async ({ formId }, personId) => {
  const payload = await listFormSubmissions(formId, { personKey: personId });
  const submissions = sortSubmissionsNewestFirst(normalizeSubmissionsResponse(payload));
  return { formId, personId, submissions };
};

const collectResponseEntries = (results, refs, personId) =>
  results.map((result, index) =>
    result.status === 'fulfilled'
      ? result.value
      : { formId: refs[index].formId, personId, submissions: [], failed: true },
  );

/**
 * Loads what Forms widgets need to render for the plan's learner:
 * - the form catalog, only once the plan has a Forms widget (Add Form forces a refresh);
 * - submissions for every form attached to each widget, for that learner (`person_key`).
 * Nothing loads without `form_view`. Submissions are cached per learner + form; a newly attached
 * form shows nothing until its own responses arrive and a detached form's late responses are
 * dropped. Each learner + form is fetched once. Every widget that has the form shows that full
 * list, including submissions made outside the widget. `reference_id` stays on each submission.
 *
 * @param {{ sections: object[], personId: number | null, canReadForms: boolean }} params
 */
export const useFormsWidgetData = ({ sections, personId, canReadForms }) => {
  const refs = useMemo(() => collectFormsWidgetRefs(sections), [sections]);
  const refsKey = buildFormsRefsKey(refs);
  const refsRef = useRef(refs);
  refsRef.current = refs;
  const hasFormsWidget = useMemo(() => hasPersistedFormsWidget(sections), [sections]);

  const [catalogRows, setCatalogRows] = useState([]);
  const [catalogStatus, setCatalogStatus] = useState(CATALOG_STATUS.IDLE);
  const catalogRequestIdRef = useRef(0);
  const [responseCache, setResponseCache] = useState(EMPTY_RESPONSE_CACHE);
  const responseCacheRef = useRef(responseCache);
  responseCacheRef.current = responseCache;

  const loadCatalog = useCallback((force) => {
    const requestId = catalogRequestIdRef.current + 1;
    catalogRequestIdRef.current = requestId;
    setCatalogStatus(CATALOG_STATUS.LOADING);
    return getFormDefinitionsListCached({ force })
      .then((rows) => {
        if (catalogRequestIdRef.current !== requestId) {
          return;
        }
        setCatalogRows(Array.isArray(rows) ? rows : []);
        setCatalogStatus(CATALOG_STATUS.LOADED);
      })
      .catch(() => {
        if (catalogRequestIdRef.current === requestId) {
          setCatalogStatus(CATALOG_STATUS.ERROR);
        }
      });
  }, []);

  const refreshCatalog = useCallback(() => loadCatalog(true), [loadCatalog]);

  // Preload once per page when a Forms widget first appears.
  useEffect(() => {
    if (canReadForms && hasFormsWidget && catalogStatus === CATALOG_STATUS.IDLE) {
      loadCatalog(false);
    }
  }, [canReadForms, hasFormsWidget, catalogStatus, loadCatalog]);

  // `refsKey` changes only when a widget's attached forms change, not on every sections update.
  useEffect(() => {
    if (!canReadForms || !personId) {
      setResponseCache(EMPTY_RESPONSE_CACHE);
      return undefined;
    }

    const currentRefs = refsRef.current;
    setResponseCache((cache) => pruneResponseCache(cache, currentRefs, personId));
    const pending = selectPendingRefs(responseCacheRef.current, currentRefs, personId);
    if (pending.length === 0) {
      return undefined;
    }

    let cancelled = false;
    Promise.allSettled(pending.map((ref) => fetchFormSubmissions(ref, personId))).then((results) => {
      if (cancelled) {
        return;
      }
      setResponseCache((cache) =>
        mergeResponseEntries(
          cache,
          collectResponseEntries(results, pending, personId),
          refsRef.current,
          personId,
        ),
      );
      if (results.some((result) => result.status === 'rejected')) {
        notifyFormsWidgetError(FORMS_WIDGET_MESSAGES.RESPONSES_LOAD_FAILED);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [refsKey, personId, canReadForms]);

  const submissionsByWidgetId = useMemo(
    () => selectCurrentSubmissions(responseCache, refs, personId),
    [responseCache, refs, personId],
  );

  const exportResponse = useCallback(
    async (widgetId, formId, responseId) => {
      const submission = findSubmissionById(submissionsByWidgetId[widgetId]?.[formId], responseId);
      if (!submission) {
        return;
      }

      try {
        const formDefinition = await loadFormDefinitionWithFallback(formId);
        const formTitle = String(formDefinition?.name ?? '');
        await exportFormSubmissionAsPdf({ submission, formDefinition, formTitle });
      } catch {
        store.dispatch(showToast(FORM_RESPONSE_PDF_EXPORT_ERROR));
      }
    },
    [submissionsByWidgetId],
  );

  const catalog = useMemo(
    () => ({
      rows: catalogRows,
      byId: indexCatalogById(catalogRows),
      loaded:
        catalogStatus === CATALOG_STATUS.LOADED ||
        catalogStatus === CATALOG_STATUS.ERROR,
      loading:
        hasFormsWidget &&
        (catalogStatus === CATALOG_STATUS.IDLE || catalogStatus === CATALOG_STATUS.LOADING),
      error: catalogStatus === CATALOG_STATUS.ERROR ? FORMS_WIDGET_MESSAGES.CATALOG_LOAD_FAILED : null,
    }),
    [catalogRows, catalogStatus, hasFormsWidget],
  );

  return { catalog, submissionsByWidgetId, refreshCatalog, exportResponse };
};
